import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import Head from '../../layout/head/Head';
import { Block, BlockContent, BlockDes, BlockHead, BlockTitle, Button, Icon, PreviewCard } from '../../components/Component';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { Alert, Spinner } from 'reactstrap';
import TextInput from '../components/inputs/TextInput';
import { useDispatch } from 'react-redux';
import AuthFooter from './AuthFooter';
import { fail_verify_sales_rep_account, loading_verify_sales_rep_account, reset_verify_sales_rep_account, success_verify_sales_rep_account } from '../../reducers/no_auth_tenant/verify_sales_rep_account_Slice';
import { messageToast } from '../../utils/Utils';
import { success_set_masked_credentials } from '../../reducers/no_auth_tenant/set_masked_credentials_Slice';
import { useSelector } from 'react-redux';
import * as app_consts from "../../utils/app_consts";
import { tenant_no_auth_actions_helper } from '../../actions/actions_helper';
import { VERIFY_USERNAME_ERROR_MSG, VERIFY_USERNAME_SUCCESS_MSG } from '../../utils/error_success_messages';

const initialState = { username: "" };

// Animation variants (matching TenantAuth)
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      damping: 15,
      stiffness: 120,
      duration: 0.5
    }
  }
};

const cardHover = {
  y: -5,
  boxShadow: "0 15px 30px -10px rgba(0,0,0,0.15)",
  transition: { type: "spring", stiffness: 300 }
};

const buttonHover = {
  scale: 1.03,
  boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
  transition: { type: "spring", stiffness: 400, damping: 10 }
};

const buttonTap = {
  scale: 0.98
};

const WebStoreAuth = ({ tenant_info }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialState);
  const inputRef = useRef(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    setError("");
    dispatch(tenant_no_auth_actions_helper(
      loading_verify_sales_rep_account,
      success_verify_sales_rep_account,
      fail_verify_sales_rep_account,
      VERIFY_USERNAME_ERROR_MSG,
      VERIFY_USERNAME_SUCCESS_MSG,
      app_consts.GET,
      `/${tenant_info?.url_prefix}/verify_sales_rep_account/${formData.username}`
    ));
  };

  const verify_sales_rep_account_listener = useSelector((state) => state.verify_sales_rep_account.verify_sales_rep_account);

  useEffect(() => {
    if (verify_sales_rep_account_listener.status !== app_consts.IDLE)
    {
      if (verify_sales_rep_account_listener.status === app_consts.LOADING)
      {
        setLoading(true);
        setError("");
      } else
      {
        setLoading(false);
        if (verify_sales_rep_account_listener.status === app_consts.FAIL)
        {
          setError(verify_sales_rep_account_listener.message, app_consts.FAIL);
          messageToast(verify_sales_rep_account_listener.message, app_consts.FAIL);
        } else if (verify_sales_rep_account_listener.status === app_consts.SUCCESS)
        {
          dispatch(success_set_masked_credentials(verify_sales_rep_account_listener.data));
          navigate(`/${tenant_info.url_prefix}/webstore/auth/send_otp`);
        }
        dispatch(reset_verify_sales_rep_account());
      }
    }
  }, [verify_sales_rep_account_listener]);

  return (
    <>
      <Head title={tenant_info?.name ? `${tenant_info.name} Login` : "Login"} />
      <Block className="nk-block-middle nk-auth-body wide-xs py-5">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="auth-container"
        >
          <motion.div variants={itemVariants} className="text-center mb-4">
            <Link to={process.env.PUBLIC_URL + `/${tenant_info?.url_prefix}`} className="logo-link d-inline-block">
              {tenant_info?.tenant_logo && (
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <img className="logo-light logo-img logo-img-website" src={tenant_info?.tenant_logo} alt="logo" />
                  <img className="logo-dark logo-img logo-img-website" src={tenant_info?.tenant_logo} alt="logo-dark" />
                </motion.div>
              )}
            </Link>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              whileHover={cardHover}
            >
              <PreviewCard
                className="card-bordered"
                bodyClass="card-inner-lg p-5"
                style={{
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)',
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)'
                }}
              >
                <BlockHead className="text-center mb-4">
                  <BlockContent>
                    <motion.div variants={itemVariants}>
                      <BlockTitle tag="h3" className="mb-1" style={{ color: '#3b3b3b', fontWeight: '700' }}>
                        {tenant_info?.name}
                      </BlockTitle>
                    </motion.div>
                    <motion.div variants={itemVariants}>
                      <BlockTitle tag="h4" className="fw-bold mt-3" style={{ color: '#2c3e50' }}>
                        Webstore Sign In
                      </BlockTitle>
                    </motion.div>
                    <motion.div variants={itemVariants}>
                      <BlockDes>
                        <p className="text-muted" style={{ fontSize: '0.95rem' }}>
                          Sign in to continue to your account
                        </p>
                      </BlockDes>
                    </motion.div>
                  </BlockContent>
                </BlockHead>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ type: "spring" }}
                  >
                    <Alert
                      color="danger"
                      className="alert-icon mb-4 text-center"
                      style={{ borderRadius: '8px' }}
                    >
                      <Icon name="alert-circle" /> {error}
                    </Alert>
                  </motion.div>
                )}

                <form className="is-alter" onSubmit={(e) => e.preventDefault()}>
                  <motion.div variants={itemVariants} className="mb-4">
                    <TextInput
                      label="Email Address or Mobile Number"
                      value={formData.username}
                      colSize="12"
                      name="username"
                      handleChange={handleChange}
                      inputRef={inputRef}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        padding: '12px 15px',
                        fontSize: '0.95rem'
                      }}
                    />
                  </motion.div>

                  <motion.div variants={itemVariants} className="form-group">
                    <motion.div
                      whileHover={buttonHover}
                      whileTap={buttonTap}
                    >
                      <Button
                        onClick={handleSubmit}
                        size="lg"
                        className="btn-block fw-bold"
                        type="submit"
                        color="primary"
                        disabled={loading || !formData.username}
                        style={{
                          borderRadius: '8px',
                          padding: '12px',
                          fontSize: '1rem',
                          letterSpacing: '0.5px',
                          border: 'none',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}
                      >
                        {loading ? (
                          <>
                            <Spinner
                              size="sm"
                              color="light"
                              className="mr-2"
                              style={{
                                width: '1rem',
                                height: '1rem',
                                borderWidth: '0.15em'
                              }}
                            />
                            Signing In...
                          </>
                        ) : (
                          <>
                            <Icon name="unlock" className="mr-2" />
                            Sign In
                          </>
                        )}
                      </Button>
                    </motion.div>
                  </motion.div>
                </form>

                <motion.div
                  variants={itemVariants}
                  className="form-note-s2 text-center pt-4"
                  whileHover={{ scale: 1.02 }}
                >
                  <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                    Looking for the Employee Sign In?{' '}
                    <Link
                      to={`${process.env.PUBLIC_URL}/${tenant_info?.url_prefix}/auth`}
                      style={{
                        color: '#4a6cf7',
                        fontWeight: '500',
                        textDecoration: 'none',
                        borderBottom: '1px dashed #4a6cf7'
                      }}
                    >
                      Click here
                    </Link>
                  </p>
                </motion.div>
              </PreviewCard>
            </motion.div>
          </motion.div>
        </motion.div>
      </Block>
      <AuthFooter tenant_name={tenant_info?.name} />
    </>
  );
};

export default WebStoreAuth;